import { useState } from "react";
import { Button, Modal } from "antd";
import { Download, FileText, FileSpreadsheet, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  downloadExcel,
  downloadPdf,
  getColumnSet,
} from "../../utils/dataDownload";

// Unified "Download Data" button for NoticeAI list pages.
//
// Behaviour (per product spec):
//   - Default label: "Download Data" + count when visibleCount supplied.
//   - When the user has selected rows: "Download Selected Data (N)".
//   - Click ALWAYS opens a confirmation popup (Yes / No) describing exactly
//     what will be downloaded — either ALL records matching the current
//     filters, or just the selected ones. "Yes" proceeds to a format choice
//     (Excel / PDF); the file is then generated client-side.
//   - The format-choice modal uses the legacy fancy "Export Data" design
//     (red PDF card + green Excel card + animated dot footer) instead of
//     the plain Antd buttons, matching what was previously rendered by
//     TableV2's internal export overlay.
//
// Props:
//   entity        — column-set key (see utils/dataDownload getColumnSet),
//                   e.g. "clients-income-tax", "notices-gst".
//   noun          — plural noun for the confirm copy: "clients" | "notices".
//   selectedIds   — string[] currently-selected row ids.
//   selectedRows  — the selected record objects (used directly when present
//                   so we don't refetch for a selection download).
//   fetchAllRecords — async () => record[]; returns EVERY record matching
//                   the current filters (the page already loops its list
//                   endpoint for this — reuse that fn).
//   visibleCount  — number of records currently visible in the table (used
//                   in the button label + format-choice footer). Optional;
//                   when omitted the count is dropped from the label and
//                   the footer falls back to a generic copy.
//   fileBaseName  — base name for the downloaded file (date is appended).
//   title         — sheet/PDF title.
const DownloadDataButton = ({
  entity,
  noun = "records",
  selectedIds = [],
  selectedRows = null,
  fetchAllRecords,
  visibleCount,
  fileBaseName = "noticeai-export",
  title = "Data Export",
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasSelection = selectedIds.length > 0;
  const hasVisibleCount = typeof visibleCount === "number";

  // Label policy: show count only when the user has actually selected rows
  // (the selection count is genuinely helpful — "Download Selected Data (5)").
  // No count by default — the fancy "Export Data" modal footer carries the
  // current-view count anyway, so doubling it on the button is noisy.
  const label = hasSelection
    ? `Download Selected Data (${selectedIds.length})`
    : "Download Data";

  // Confirmation copy — clear, full-sentence English, count-aware. When
  // visibleCount is supplied (current-view row count), embed it so the user
  // sees exactly how many records will be downloaded before saying Yes.
  const confirmMessage = hasSelection
    ? `You have selected ${selectedIds.length} ${noun}. Do you want to download the data for the selected ${noun}?`
    : `No ${noun} are selected. Do you want to download the data for all ${noun}?`;

  // Footer copy for the fancy "Export Data" modal — mirrors the legacy
  // TableV2 overlay so the UX stays familiar.
  const exportFooterMessage = hasSelection
    ? `Exporting ${selectedIds.length} selected ${noun}`
    : hasVisibleCount
    ? `Exporting ${visibleCount} ${noun} (matching current view)`
    : `Exporting all ${noun} (matching current filters)`;

  const resolveRecords = async () => {
    if (hasSelection && Array.isArray(selectedRows) && selectedRows.length > 0) {
      // Prefer the in-memory selected rows. Fall back to filtering the full
      // set by id when the selection spans pages (selectedRows only holds
      // the current page's objects).
      const haveAll = selectedIds.every((id) =>
        selectedRows.some((r) => (r.id || r.name) === id)
      );
      if (haveAll) {
        return selectedRows.filter((r) =>
          selectedIds.includes(r.id || r.name)
        );
      }
    }
    const all = (await fetchAllRecords?.()) || [];
    if (hasSelection) {
      const sel = new Set(selectedIds);
      return all.filter((r) => sel.has(r.id || r.name));
    }
    return all;
  };

  const handleConfirmYes = () => {
    setConfirmOpen(false);
    setFormatOpen(true);
  };

  const runDownload = async (format) => {
    setBusy(true);
    try {
      const records = await resolveRecords();
      if (!records || records.length === 0) {
        toast.info(`No ${noun} to download.`);
        return;
      }
      const columnSet = getColumnSet(entity);
      const stamp = new Date().toISOString().slice(0, 10);
      const scope = hasSelection ? "selected" : "all";
      const fileName = `${fileBaseName}-${scope}-${stamp}`;
      if (format === "excel") {
        downloadExcel(records, columnSet, { fileName, title });
      } else {
        downloadPdf(records, columnSet, { fileName, title });
      }
      toast.success(
        `Downloaded ${records.length} ${noun} as ${format === "excel" ? "Excel" : "PDF"}.`
      );
      setFormatOpen(false);
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : `Failed to download ${noun} data.`
      );
    } finally {
      setBusy(false);
    }
  };

  const closeFormatModal = () => {
    if (busy) return;
    setFormatOpen(false);
  };

  return (
    <>
      <Button
        type="primary"
        icon={<Download size={16} />}
        onClick={() => setConfirmOpen(true)}
      >
        {label}
      </Button>

      {/* Yes / No confirmation */}
      <Modal
        open={confirmOpen}
        title="Download data"
        onCancel={() => setConfirmOpen(false)}
        onOk={handleConfirmYes}
        okText="Yes"
        cancelText="No"
      >
        <p className="text-gray-700">{confirmMessage}</p>
      </Modal>

      {/* Format choice — legacy fancy "Export Data" design ported from
          TableV2 internal export overlay. Custom HTML/CSS (not Antd Modal)
          so the gradient cards + animated footer dot render identically to
          how they used to. Backdrop click closes (unless a download is
          in progress to avoid losing the user mid-export). */}
      {formatOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998] animate-in fade-in duration-200"
            onClick={closeFormatModal}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in duration-200">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-800">
                    Export Data
                  </h3>
                  <button
                    onClick={closeFormatModal}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Choose your preferred export format
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-3">
                {/* PDF Export Option */}
                <button
                  onClick={() => runDownload("pdf")}
                  disabled={busy}
                  className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl hover:border-red-400 hover:shadow-lg transition-all duration-200 group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-800 text-base">
                      Download as PDF
                    </h4>
                    <p className="text-sm text-gray-600">
                      Export {noun} in PDF format
                    </p>
                  </div>
                  <Download className="w-5 h-5 text-red-500 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Excel Export Option */}
                <button
                  onClick={() => runDownload("excel")}
                  disabled={busy}
                  className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl hover:border-green-400 hover:shadow-lg transition-all duration-200 group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileSpreadsheet className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-800 text-base">
                      Download as Excel
                    </h4>
                    <p className="text-sm text-gray-600">
                      Export {noun} in Excel (.xlsx) format
                    </p>
                  </div>
                  <Download className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  <span>
                    {busy ? `Generating file…` : exportFooterMessage}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DownloadDataButton;
